import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Navigation, Share2 } from "lucide-react";
import { Reveal, SectionHeading, Accent } from "@/components/Reveal";
import { SocialLinks } from "@/components/SocialLinks";
import { useLang } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", company: "", service: "", message: "" };

const inputCls =
  "w-full bg-coal border border-line text-mist px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300 placeholder:text-dim/50";

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block font-mono text-[11px] uppercase tracking-[0.25em] text-dim mb-2">
    {children}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const { t, c } = useLang();
  const tc = t.contact;

  const INFO = [
    { icon: MapPin, label: tc.address, value: c.CONTACTS.address, testid: "contact-info-address" },
    { icon: Phone, label: tc.phone, value: c.CONTACTS.phone, testid: "contact-info-phone" },
    { icon: Mail, label: tc.email, value: c.CONTACTS.email, testid: "contact-info-email" },
    { icon: Clock, label: tc.hours, value: c.CONTACTS.hours, testid: "contact-info-hours" },
  ];

  useEffect(() => {
    const onQuote = (e) => {
      const { message, service } = e.detail;
      setForm((f) => ({ ...f, message, service: c.SERVICE_OPTIONS.includes(service) ? service : f.service }));
      toast(tc.quoteAdded);
    };
    window.addEventListener("calc-quote", onQuote);
    return () => window.removeEventListener("calc-quote", onQuote);
  }, [c, tc.quoteAdded]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success(tc.success);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
      <SectionHeading kicker={tc.kicker} title={<Accent parts={tc.title} />} testid="contact-heading" />

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-dim leading-relaxed mb-10 max-w-md">{tc.intro}</p>
            <div className="flex flex-col divide-y divide-line border-y border-line">
              {INFO.map((row) => (
                <div key={row.testid} data-testid={row.testid} className="flex items-center gap-5 py-5">
                  <row.icon size={18} className="text-gold shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim">{row.label}</p>
                    <p className="text-mist text-sm mt-1">{row.value}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-5 py-5" data-testid="contact-info-socials">
                <Share2 size={18} className="text-gold shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-dim mb-3">{tc.follow}</p>
                  <SocialLinks prefix="contact" />
                </div>
              </div>
            </div>

            <div className="mt-8 border border-line overflow-hidden">
              <iframe
                data-testid="contact-map"
                title={tc.mapTitle}
                src="https://www.openstreetmap.org/export/embed.html?bbox=24.7340%2C42.1575%2C24.7600%2C42.1685&layer=mapnik&marker=42.16289%2C24.74705"
                className="w-full h-64 md:h-72 border-0 block"
                style={{ filter: "grayscale(0.85) invert(0.92) contrast(0.85)" }}
                loading="lazy"
              />
            </div>
            <a
              data-testid="contact-navigate-button"
              href="https://www.google.com/maps/dir/?api=1&destination=%D0%B1%D1%83%D0%BB.+%22%D0%94%D1%83%D0%BD%D0%B0%D0%B2%22+196%D0%90%2C+%D0%9F%D0%BB%D0%BE%D0%B2%D0%B4%D0%B8%D0%B2"
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-4 inline-flex items-center gap-3 border border-gold/60 text-gold px-6 py-3 text-sm tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Navigation size={15} />
              {tc.navigate}
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="border border-line bg-ink p-8 md:p-10 grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="cf-name">{tc.name}</Label>
                <input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder={tc.namePh} />
              </div>
              <div>
                <Label htmlFor="cf-email">{tc.email} *</Label>
                <input id="cf-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder={tc.emailPh} />
              </div>
              <div>
                <Label htmlFor="cf-phone">{tc.phone}</Label>
                <input id="cf-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={tc.phonePh} />
              </div>
              <div>
                <Label htmlFor="cf-company">{tc.company}</Label>
                <input id="cf-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder={tc.companyPh} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cf-service">{tc.service}</Label>
                <select id="cf-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                  <option value="">{tc.servicePh}</option>
                  {c.SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="cf-message">{tc.message}</Label>
                <textarea id="cf-message" data-testid="contact-message-input" required minLength={5} rows={form.message.includes("\n") ? 9 : 4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder={tc.messagePh} />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 bg-gold text-ink px-8 py-4 text-sm font-medium tracking-wide hover:bg-gold-light transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? tc.sending : tc.submit}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
